import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Wrench, Droplet, Battery } from 'lucide-react';
import { Button } from './ui/button';

/**
 * "Latest Car Care Tips" homepage preview strip.
 *
 * The Soro embed at /blog renders inside an opaque widget script and does not
 * expose a public JSON/RSS feed for the embed token. To avoid faking blog
 * content, we render a static CTA with topic chips that links to /blog.
 */
export const LatestCarCareTips = () => {
  const topics = [
    { icon: Droplet, label: 'Oil Changes' },
    { icon: Wrench, label: 'Tires & Brakes' },
    { icon: Battery, label: 'Batteries & Roadside' },
  ];

  return (
    <section
      id="latest-car-care-tips"
      className="bg-zinc-950 text-white py-16 sm:py-20 border-t border-yellow-500/20"
      data-testid="latest-tips-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/15 border border-red-600/40 text-red-400 text-xs uppercase tracking-widest font-semibold mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            From the Vargas Car Care Blog
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
            Latest{' '}
            <span className="text-yellow-400">Car Care Tips</span>
          </h2>

          <p className="mt-5 text-base sm:text-lg text-gray-300 max-w-2xl mx-auto">
            Fresh advice on oil changes, tires, brakes, batteries, and roadside
            emergencies in Jacksonville.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          {topics.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="bg-black/60 border border-zinc-800 rounded-xl p-5 flex items-center gap-3 hover:border-yellow-500/40 transition-colors"
              data-testid={`tip-topic-${label.toLowerCase().replace(/\s|&/g, '-')}`}
            >
              <span className="w-10 h-10 rounded-lg bg-red-600/15 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-red-500" />
              </span>
              <p className="text-sm sm:text-base font-semibold text-gray-100">
                {label}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-black/40 border border-zinc-800 rounded-xl p-6 sm:p-8 text-center">
          <p className="text-base sm:text-lg text-gray-200">
            Visit our blog for the latest car care tips and roadside advice.
          </p>
          <div className="mt-5">
            <Link to="/blog">
              <Button
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-7 py-6 text-base font-semibold shadow-lg shadow-yellow-500/20"
                data-testid="read-car-care-tips-btn"
              >
                Read Car Care Tips
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestCarCareTips;
